import { Component } from 'react'
import PropTypes from 'prop-types'
import queryString from 'query-string'

import toast from 'lib/toast'
import { withAuthContext } from 'context/with-auth-context'
import AccountSetupForm from 'components/authentication/registration/free-edition/AccountSetupForm'
import AccountNameConflictForm from 'components/authentication/registration/free-edition/AccountNameConflictForm'
import api from 'api'
import { messageFromResponseError } from 'utils/strings'

const initialState = {
  email: '',
  firstName: '',
  lastName: '',
  role: '',
  customRole: '',
  roleHasChanged: false,
  code: '',
  password: '',
  confirmPassword: '',
  companyName: '',
  teamName: '',
  allowUsStorage: false,
  isActivating: false,
  isFetchingUserInfo: false,
  conflict: false,
  inviteRequested: false
}

class AccountSetup extends Component {
  static propTypes = {
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    authState: PropTypes.object.isRequired
  }

  constructor(props) {
    super()
    const { location, history } = props

    const { email, first_name, last_name, provider, code } = queryString.parse(
      location.search
    )

    this.state = initialState

    if (!code || !email) return history.push('/signup')

    this.state = {
      ...this.state,
      email,
      firstName: first_name,
      lastName: last_name,
      code,
      provider
    }
  }

  componentDidMount() {
    if (this.emailInputRef) this.emailInputRef.focus()
  }

  handlePropChange = (prop, val, callback) =>
    this.setState({ [prop]: val }, callback)

  generateHandlePropChange = prop => (value, callback) =>
    this.handlePropChange(prop, value, callback)

  handleRoleChange = value => {
    this.setState({
      role: value,
      roleHasChanged: true
    })
  }

  handleCustomRoleChange = customRole => {
    this.setState({ customRole })
  }

  handleBack = () => this.props.history.push('/')

  handleUnsetConflicted = async () => {
    this.setState({ conflict: false })
  }

  handleSignUp = async () => {
    this.setState({ isActivating: true })

    const { authState } = this.props
    const {
      email,
      password,
      companyName,
      teamName,
      role,
      customRole,
      code,
      provider,
      conflict
    } = this.state

    try {
      let response
      let genericBody = {
        email,
        code,
        role: role === 'Other' ? customRole : role,
        companyName,
        teamName,
        allowUsStorage: true,
        isRetry: conflict
      }

      if (provider === 'google') {
        response = await api.users.freeEdition.activateGoogleUser(genericBody)
      } else {
        response = await api.users.freeEdition.activatePasswordUser({
          ...genericBody,
          password
        })
      }

      await new Promise(resolve => this.setState(initialState, resolve))
      await authState.startSession(response.session)
    } catch (err) {
      const conflict = err?.response && err.response.status === 409
      if (!conflict) {
        toast({
          type: 'error',
          time: 6000,
          text: messageFromResponseError(
            err,
            'Unable to sign up. Please try again later.'
          )
        })
      }
      this.setState({ conflict })
    } finally {
      if (this.state.isActivating) this.setState({ isActivating: false })
    }
  }

  handleInviteRequest = async () => {
    this.setState({ isActivating: true })

    const { email, companyName, code } = this.state

    try {
      await api.company.requestInvite({
        company_name: companyName,
        code,
        email
      })
      this.setState({ inviteRequested: true })
    } catch (err) {
      toast({
        type: 'error',
        time: 6000,
        text: messageFromResponseError(
          err,
          'Unable to sign up. Please try again later.'
        )
      })
    } finally {
      if (this.state.isActivating) this.setState({ isActivating: false })
    }
  }

  setEmailInputRef = ref => {
    this.emailInputRef = ref
  }

  render() {
    const {
      email,
      firstName,
      lastName,
      companyName,
      teamName,
      customRole,
      role,
      roleHasChanged,
      password,
      confirmPassword,
      allowUsStorage,
      provider,
      isActivating,
      conflict,
      inviteRequested
    } = this.state

    return conflict ? (
      <AccountNameConflictForm
        companyName={companyName}
        disabled={isActivating}
        handleCompanyNameChange={this.generateHandlePropChange('companyName')}
        handleInviteRequest={this.handleInviteRequest}
        handleSignUp={this.handleSignUp}
        handleUnsetConflicted={this.handleUnsetConflicted}
        inviteRequested={inviteRequested}
      />
    ) : (
      <AccountSetupForm
        allowUsStorage={allowUsStorage}
        companyName={companyName}
        confirmPassword={confirmPassword}
        customRole={customRole}
        disabled={isActivating}
        email={email}
        firstName={firstName}
        handleAllowUsStorageChange={this.generateHandlePropChange(
          'allowUsStorage'
        )}
        handleBack={this.handleBack}
        handleCompanyNameChange={this.generateHandlePropChange('companyName')}
        handleConfirmPasswordChange={this.generateHandlePropChange(
          'confirmPassword'
        )}
        handleCustomRoleChange={this.handleCustomRoleChange}
        handlePasswordChange={this.generateHandlePropChange('password')}
        handleRoleChange={this.handleRoleChange}
        handleSignUp={this.handleSignUp}
        handleTeamNameChange={this.generateHandlePropChange('teamName')}
        lastName={lastName}
        password={password}
        provider={provider && provider.toLowerCase()}
        role={role}
        roleHasChanged={roleHasChanged}
        setEmailInputRef={this.setEmailInputRef}
        teamName={teamName}
      />
    )
  }
}

export default withAuthContext(AccountSetup)
